import React, { useContext, useEffect, useRef, useState } from 'react';
import { ResourceStrings } from '../../../ResourceStrings';
import styled from 'styled-components/macro';
import { NotificationInfo } from '../../../store/appState';
import { cssVarStr } from '../../../utils/react';
import { App, SignInState } from '../../../app';
import Button from '../../shared/Button';
import Flex from '../../shared/Flex';
import { Icon } from '@fluentui/react/lib/Icon';
import ShareLinkDialog from '../../shared/ShareLinkDialog';
import { HashLocatorContext } from '../../shared/HashLocator';
import { useUrlFragment } from '../../../hooks/routing';
import Panel from '../../shared/Panel';
import { darken } from 'polished';
import { Permission, UserRole } from '../../../config/permissions';
import { hasPermission } from '../../../config/accessPolicies';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { CommandButton } from '@fluentui/react/lib/Button';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import * as Actions from '../../../store/actions';
import ConvertSolutionOrWorkaroundDialog from "./ConvertSolutionDialog";
import Reactivate from './Reactivate';
import { TicketContext } from './TicketContext';
import { ticketNotificationKey } from '../../../store/actionsHandler';
import { NodeVisibility, VoteType } from '../../../store/actions';
import { ReactivationConfig } from './reactivationRules';
import VisibilityTransition from '../../shared/VisibilityTransition';
import CommentEditor, { NodeEditorOperation } from './CommentEditor';
import { generateTrackingId } from '../../../utils/tools';
import { keepFormOpen, operationIsSubmitting, ticketOperationKey, UIOperationState, useOperation } from '../../../store/operation';
import OperationNotification from './OperationNotification';
import { ensureSignedIn, restrictUrlParams } from '../../../utils/webPage';
import { TimelineItem, TimelineItemType } from '../../../hooks/posts';
import FeedbackTimelineComponent, { ElementColor } from './FeedbackTimelineComponent';
import { NodeType } from './RichNodeEditor';
import { hashForNode, hashForNodeDirect, isPrefixForPinnedSection } from '../../../constants/urlParams';
import { forMinWidth } from '../../../theme/breakpoints';
import { ChildPost, HistoryItem, PrincipalId, ReactionType, RootPost, PostType } from '../../../RootPost';
import HiddenTextInfo from './HiddenTextInfo';
import HtmlPreview from '../../shared/HtmlPreview';
import { instance } from '../../../config/serverConfig';

export const fixedSolutionBorderColor = "#17873e";

export const isWorkaround = (childPost: ChildPost): boolean =>
    childPost?.type === PostType.workaround;

const solutionName = (solution: ChildPost, ticketState: string): string => {
    const couldReactivateEver = App.CouldReactivateSolutionEver(solution);

    if (isWorkaround(solution)) {
        return couldReactivateEver ?
            (instance.isInternal ? "Official Workaround" : "Microsoft Workaround") :
            "Workaround";
    }
    else {
        if (couldReactivateEver) {
            // can there be more than one Microsoft solution/resolution in same same ticket
            // if yes then should this be state of ticket when that solution was added???
            return ticketState === "fixed" ?
                (instance.isInternal ? "Official Solution" : "Microsoft Solution") :
                (instance.isInternal ? "Official Resolution" : "Microsoft Resolution");
        } else {
            return "Solution";
        }
    }
}

export const solutionTitleColors = (solution: ChildPost, ticket: RootPost): ElementColor => {
    const couldReactivateEver = App.CouldReactivateSolutionEver(solution);

    if (couldReactivateEver) {
        if (ticket.state?.indexOf("fixed") !== -1) {
            return {
                backgroundColor: "var(--color-solution-microsoft-fixed)",
                textColor: "white"
            };
        }
        else {
            return {
                backgroundColor: "var(--color-solution-microsoft)",
                textColor: "white"
            };
        }
    }

    return {
        backgroundColor: "var(--color-solution)",
        textColor: "white"
    };
}

export interface RichSolutionProps {
    idPrefix: string;
    item: ChildPost;
    timelineItem?: TimelineItem;
    isExpandedByDefault?: boolean;
    onEdit?: (solution: ChildPost) => void;
}

export default function RichSolution(props: RichSolutionProps): JSX.Element {
    const { item, isExpandedByDefault } = props;
    const ticketDetailsCtx = useContext(TicketContext);
    const ticket = ticketDetailsCtx.ticketDetails.ticket?.data;
    const signInState = useSelector((state: RootState) => state.appState.signInState);
    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);

    const solnId = hashForNode(item.id, props.idPrefix);
    // id to reach nested node without specifying parents, not needed for pinned items
    const solnIdDirect = !isPrefixForPinnedSection(props.idPrefix) ? hashForNodeDirect(item.id) : undefined;
    const fullUrl = useUrlFragment(solnId);
    const shareableUrl = restrictUrlParams(fullUrl, []);

    const { hash } = window.location;
    const expandSolutionComments = hash.startsWith(`#${solnId}-`) || hash.startsWith(`#T-ND`);

    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [reactivateEditInfo, setReactivateEditInfo] = useState<ReactivationConfig>(null);
    const [showConvertDialog, setShowConvertDialog] = useState(false);  // Should we keep this on parent or redux ...
    const [commentsExpanded, setCommentsExpanded] = useState(isExpandedByDefault || expandSolutionComments);
    const isUserBanned = useSelector((state: RootState) => state.appState.isUserBanned);
    const userRoles = useSelector((state: RootState) => state.appState.userRoles);
    const userVsid = useSelector((state: RootState) => state.appState.userVsid);
    const [newCommentTrackingId, setNewCommentTrackingId] = useState<string>(null);

    const solutionOpKey = ticketOperationKey(ticket?.id, item.id);
    const solutionOperation = useOperation(solutionOpKey);

    const reactNoOpKey = ticketOperationKey(ticket?.id, item.id, "reactno");
    const reactNoOperation = useOperation(reactNoOpKey);

    const hashLocatorCtx = useContext(HashLocatorContext);
    useEffect(() => hashLocatorCtx.newContentWasLoaded(), []);

    const isAnyOperationInProgress = solutionOperation.isInProgress() || reactNoOperation.isInProgress();

    const solutionMenuProps = (solution: ChildPost): IContextualMenuItem[] => [
        {
            key: "Share",
            text: "Share",
            onClick: () => setShowLinkDialog(true)
        },
        {
            key: "Edit",
            text: "Edit",
            disabled: isInReadOnlyMode || isUserBanned || isAnyOperationInProgress,
            iconProps: { iconName: 'SingleColumnEdit' },
            data: {
                requiredPermission: Permission.Solution_Edit,
            },
            onClick: () => props?.onEdit(solution)
        },
        {
            key: "flagForModeration",
            text: "Report content",
            disabled: isInReadOnlyMode || isAnyOperationInProgress,
            data: {
                requiredPermission: Permission.Solution_FlagForModeration,
            },
            onClick: function () {
                solutionOperation.resetOperation();
                solutionOperation.setState(UIOperationState.InProgress);
                Actions.flagForModeration(item.id, ticket?.id, solutionOpKey);
            }
        },
        {
            key: "Convert",
            text: "Convert",
            disabled: isInReadOnlyMode || isUserBanned || isAnyOperationInProgress,
            data: {
                requiredPermission: Permission.Solution_Convert,
            },
            onClick: () => setShowConvertDialog(true)
        },
        {
            key: "Delete",
            text: "Delete",
            disabled: isInReadOnlyMode || isAnyOperationInProgress,
            data: {
                requiredPermission: Permission.Solution_Delete,
            },
            onClick: function () {
                solutionOperation.resetOperation();
                solutionOperation.setState(UIOperationState.InProgress);
                Actions.deleteNode(item.id, solutionOpKey);
            }
        },
    ];

    const getRolesOfCurrentUerForSolution = (solution: ChildPost, currentUserId: PrincipalId): UserRole[] =>
        (solution.author.id === currentUserId) ? [UserRole.Author] : [];

    const getPermittedSolutionMenuItems = (solution: ChildPost, currentUserId: PrincipalId, userRoles: UserRole[]): IContextualMenuItem[] => {
        const contextSpecificRoles = getRolesOfCurrentUerForSolution(solution, currentUserId);
        const allAvailableRoles = [...userRoles, ...contextSpecificRoles];

        return solutionMenuProps(solution)
            .filter(x => hasPermission(x.data?.requiredPermission, allAvailableRoles));
    }

    const reasonCantVote = (solution: ChildPost, currentUserId: PrincipalId, signInState: SignInState, voteType: VoteType): string => {
        if (voteType === solution.userVoteType) {
            return "You have already voted for this solution";
        }

        if (signInState !== SignInState.Success) {
            return ResourceStrings.authNeeded_Vote();
        }

        if (solution.author.id === currentUserId) {
            return ResourceStrings.youCreatedIt();
        }

        return null;
    }

    const onVoteClick = (solution: ChildPost, currentUserId: PrincipalId, signInState: SignInState, voteType: VoteType): void => {
        const reason = reasonCantVote(solution, currentUserId, signInState, voteType);
        if (reason) {
            Actions.setContextualNotification(ticketNotificationKey(ticket?.id), {
                title: reason,
                type: "information",
                isCritical: true
            } as NotificationInfo)
        } else {
            solutionOperation.resetOperation();
            solutionOperation.setState(UIOperationState.InProgress);
            Actions.voteOnNode(ticket.id, solutionOpKey, solution.id, voteType);
        }
    }

    const onReactNoClicked = (solution: ChildPost, rule: ReactivationConfig): void => {
        if (ensureSignedIn(signInState, ticketNotificationKey(ticket?.id), "Please sign in to reactivate solution.")) {
            setNewCommentTrackingId(generateTrackingId());
            reactNoOperation.resetOperation();
            reactNoOperation.setState(UIOperationState.InProgress);
            setReactivateEditInfo(rule);
        }
    }

    const onSaveNoReactivation = (nodeType: NodeType, commentText: string, visibility: NodeVisibility, newNodeTrackingId: string): void => {
        Actions.reactivate(ticket.id, reactNoOpKey, item.id, ReactionType.react_no, visibility, newNodeTrackingId, commentText);
    }

    const cancelReactNo = (): void => {
        reactNoOperation.resetOperation();
        setReactivateEditInfo(null);
    }

    const associatedItem = props.timelineItem?.associatedItem;
    const associatedStateChange: HistoryItem | null = associatedItem?.type === TimelineItemType.stateChange ? associatedItem.data as HistoryItem : null;

    const myReactions = item.children
        .filter(x => x.author.id === ticket.currentUserId && (x.reaction === "reactYes" || x.reaction === "reactNo"));
    const reactedYes = myReactions && myReactions[myReactions.length - 1]?.reaction === "reactYes";
    const reactedNo = myReactions && myReactions[myReactions.length - 1]?.reaction === "reactNo";

    const userHasVotedUp = item.userVoteType === "voteup";
    const userHasVotedDown = item.userVoteType === "votedown";

    const couldReactivateEver = App.CouldReactivateSolutionEver(item);

    return <Wrapper>
        <Answer id={solnId} className={couldReactivateEver ? `soln-state-${ticket.state}` : ""}>
            <div id={solnIdDirect}>
                <div className={`soln-title`}>
                    <span>
                        <span>
                            <Icon iconName="EntitlementRedemption" />&nbsp;
                            <span>{solutionName(item, ticket.state)} -&nbsp;</span>
                        </span>
                        <span>
                            <span><a href={App.GetUserProfileLink(item.author.id)} target="_blank" rel="noopener noreferrer">{item.author.name}</a>&nbsp;&nbsp;</span>
                        </span>

                        {(couldReactivateEver || associatedStateChange) && (
                            <div className="changed-state">
                                <span className={`pill post-state ${associatedStateChange?.status ?? ticket?.state}`}>
                                    {App.LocalizeStateName(associatedStateChange?.status ?? ticket?.state, ticket?.type)}
                                </span>
                            </div>
                        )}
                    </span>

                    <span>
                        <CommandButton
                            className="btn-semi-compact-more-options"
                            aria-label="Show options"
                            menuProps={{
                                items: getPermittedSolutionMenuItems(item, ticket.currentUserId, userRoles)
                            }}
                            onRenderMenuIcon={() => <span></span>}
                            menuIconProps={{
                                hidden: true
                            }}
                        >
                            ···
                        </CommandButton>
                    </span>
                </div>
                <div className="soln-body">
                    <Flex column>
                        <HiddenTextInfo post={item} currentUserId={ticket.currentUserId} />

                        <HtmlPreview text={item.text} isPublic={item.isPublic} className="soln-details" />

                        {couldReactivateEver && (
                            <Reactivate
                                className="reactivate"
                                solution={item}
                                state={ticket.state}
                                isReadonly={!item.canReactivate}
                                onReactNoClicked={onReactNoClicked}
                                reactedYes={reactedYes}
                                reactedNo={reactedNo}
                            />
                        )}

                        <div className="actions-bar">
                            <span className="actions-bar-left">
                                {!couldReactivateEver && <Flex densityHor="high">
                                    <TooltipHost
                                        content={userHasVotedUp ? "Click to cancel vote" : "Click to vote up"}
                                        id={`voteup-${item.id}`}
                                    >
                                        <Button
                                            buttonType="normal"
                                            size="compact"
                                            borderless
                                            disabled={isInReadOnlyMode}
                                            aria-describedby={`voteup-${item.id}`}
                                            aria-label={userHasVotedUp ? "Click to cancel vote" : "Click to vote up"}
                                            onClick={() => onVoteClick(item, ticket.currentUserId, signInState, userHasVotedUp ? VoteType.cancelVote : VoteType.voteUp)}
                                        >
                                            <Icon iconName={userHasVotedUp ? "LikeSolid" : "Like"} />
                                        </Button>
                                    </TooltipHost>
                                    <span aria-label={`${item.voteCount} votes`}>{item.voteCount}</span>

                                    <TooltipHost
                                        content={userHasVotedDown ? "Click to cancel vote" : "Click to vote down"}
                                        id={`votedown-${item.id}`}
                                    >
                                        <Button
                                            buttonType="normal"
                                            size="compact"
                                            borderless
                                            disabled={isInReadOnlyMode}
                                            aria-describedby={`votedown-${item.id}`}
                                            aria-label={userHasVotedDown ? "Click to cancel vote" : "Click to vote down"}
                                            onClick={() => onVoteClick(item, ticket.currentUserId, signInState, userHasVotedDown ? VoteType.cancelVote : VoteType.voteDown)}
                                        >
                                            <Icon iconName={userHasVotedDown ? "DislikeSolid" : "Dislike"} />
                                        </Button>
                                    </TooltipHost>
                                </Flex>}

                                <span aria-live="polite">
                                    <Button buttonType="link" size="compact" onClick={() => setCommentsExpanded(value => !value)}>
                                        <Flex densityHor="high">
                                            <Icon iconName="Message" />
                                            <span>{`${commentsExpanded ? "Hide" : "Show"} ${couldReactivateEver ? "reactions" : "comments"} ${App.CountComments(item.children)}`}</span>
                                        </Flex>
                                    </Button>
                                </span>
                            </span>

                            <span className="light-text">{App.ParseDateTimeToLocal(item.createdDate)} </span>
                        </div>
                    </Flex>

                    <VisibilityTransition showIf={!!reactivateEditInfo && keepFormOpen(reactNoOperation.operation)}>
                        <CommentEditorWrapper>
                            <CommentEditor
                                operation={NodeEditorOperation.Add}
                                newNodeTrackingId={newCommentTrackingId}
                                title={reactivateEditInfo?.reactCommentTitle}
                                placeHolderText={reactivateEditInfo?.reactCommentPlaceholder}
                                disableAddSolution
                                disableNodeTypeChooser
                                currentUserId={ticket.currentUserId}
                                parentNodeId={item.id}
                                disabled={operationIsSubmitting(reactNoOperation.operation)}
                                isSubmitting={operationIsSubmitting(reactNoOperation.operation)}
                                onCancel={cancelReactNo}
                                onSubmit={onSaveNoReactivation}
                            />
                        </CommentEditorWrapper>
                    </VisibilityTransition>

                    <OperationNotificationWrapper
                        operation={reactNoOperation.operation}
                        resetOperation={reactNoOperation.resetOperation}
                    />

                    <OperationNotificationWrapper
                        operation={solutionOperation.operation}
                        resetOperation={solutionOperation.resetOperation}
                    />
                </div>
            </div>
        </Answer>

        {
            commentsExpanded && (
                <FeedbackTimelineComponent
                    idPrefix={solnId}
                    timelineItems={props.timelineItem.children}
                    parentNodeId={item.id}
                    currentUserId={ticket.currentUserId}
                    canContainReactions={couldReactivateEver}
                    addNewCommentButtonLabel="Add reaction"
                />
            )
        }

        <ConvertSolutionOrWorkaroundDialog
            isOpen={showConvertDialog}
            solution={item}
            ticket={ticket}
            convertFrom={isWorkaround(item) ? NodeType.workaround : NodeType.solution}
            onSubmit={(convertTo: NodeType, parentNodeId?: number) => {
                solutionOperation.resetOperation();
                solutionOperation.setState(UIOperationState.InProgress);
                Actions.convertNode(ticket.id, solutionOpKey, item.id, convertTo as NodeType, parentNodeId);
                setShowConvertDialog(false);
            }}
            onDismiss={() => setShowConvertDialog(false)}
        />

        <ShareLinkDialog
            title="Share a link to this solution"
            message="Copy and paste the URL below to share a direct link to this solution."
            url={shareableUrl}
            isOpen={showLinkDialog}
            onDismiss={() => setShowLinkDialog(false)} />
    </Wrapper>
}

const CommentEditorWrapper = styled.div`
    margin: 0;
    margin-bottom: 1rem;
    width: 100%;

    border-top: solid 1px var(--color-on-surface-lighter);
    margin-top: 3rem;
    padding-top: 1rem;
`;

const Wrapper = styled.div`
    .soln-title {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        background-color: var(--titlebar-background-color);
        border-bottom: solid 1px var(--color-on-surface-lightest);
        padding: .4rem 2rem;
        min-height: var(--timeline-header-min-height);

        &, span, a, button {
            color: var(--titlebar-text-color);
            line-height: 100%;
        }

        a {
            text-decoration: underline;
        }

        button {
            background-color: transparent;
        }

        > span {
            display: flex;
            flex-direction: column;
            padding: 0.5rem 0;
            align-self: stretch;

            > * + * {
                margin-top: .8rem;
            }

            >* {
                display: flex;
                align-items: center;
            }
        }

        *:focus {
            outline-color: var(--titlebar-text-color);
        }

        ${forMinWidth.md`
            align-items: center;
        `}

        ${forMinWidth.md`
            > span {
                flex-direction: row;
                padding: 0;

                > * + * {
                    margin-top: 0;
                }
            }
        `}
    }


    .soln-body {
        padding: 1rem 2rem 1rem;
        max-height: 100rem;
        overflow-y: auto;

        background-color: var(--timeline-node-body-background-color);

        &>div {
            display: flex;
        }

        .soln-details {
            display: block;
            word-break: break-word;
            padding-right: 2.5rem;

            p {
                margin-left: 0;
                margin-bottom: 1rem;
            }
        }

        .reactivate {
            padding-right: 2rem;
        }

        .actions-bar {
            display: flex;
            flex-direction: column;
            margin-top: 1rem;
            padding: 0;

            span, button, a {
                font-size: 1.6rem;
                font-weight: 400;
            }

            a, button {
                color: var(--color-on-surface-link);
            }

            a:disabled, button:disabled {
                color: var(--color-surface-dark1);
            }

            ${forMinWidth.md`
                flex-direction: row;
                justify-content: space-between;

                &> * + * {
                    margin-left: 3rem;
                }
            `}

            .actions-bar-left {
                display: flex;

                &> * + * {
                    margin-left: 3rem;
                }
            }
        }
    }

    .light-text {
        color: var(--color-on-background-lighter);
        font-size: 1.4rem;
    }

    .diditwork {
        font-weight: bold;
        vertical-align: middle;
        line-height: 2.5rem;

        button {
            font-weight: normal;
            padding: 0.1rem 1rem;
            margin-left: 1rem;
        }
    }

    .comments-list-wrapper.comments-list-wrapper {
        display: block;
        border-top: solid 1px var(--color-on-surface-lighter);
        background-color: ${darken(0.03, cssVarStr("--color-surface"))};
    }

    .changed-state.changed-state {
        display: flex;
        flex-direction: column;
        padding-left: 0;
        padding-right: .4rem;
        align-items: flex-start;

        ${forMinWidth.md`
            flex-direction: row;
            padding-left: 1.8rem;
            align-items: center;
        `}
    }

    .pill.pill {
        padding: .4rem .8rem;
        background-color: var(--titlebar-text-color);
        color: var(--titlebar-background-color);
        font-size: 1.2rem;
        font-weight: 600;
        border-radius: .2rem;
    }

    .soln-comment-add.soln-comment-add {
        margin-top: .5rem;
        margin-bottom: 0;
        margin-left: 0;

        .soln-comment-add-btn {
            margin-left: 3rem;
            margin-top: 2rem;
            margin-bottom: 2rem;
            font-size: 1.6rem;
        }

        .soln-comment-readonly {
            padding: 2rem 3rem;
        }

        ${CommentEditorWrapper} {
            width: auto;
        }
    }
`;

const Answer = styled(Panel)`
    display: flex;
    flex-direction: column;
    padding: 0;
    background-color: var(--color-background);
    border-radius: 0;
    box-shadow: none;

    border-radius: .5rem .5rem 0 0;
`;

const MenuButton = styled(Button)`
`;

const OperationNotificationWrapper = styled(OperationNotification)`
    margin: 2rem 1rem 1rem 1rem;
`;
